import Header from "./Header";
import Footer from "./Footer";
import ChatBot from "./ChatBot";
import "../styles/PrivacyPolicy.css";
import { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import {
    FaShieldAlt,
    FaDatabase,
    FaUserLock,
    FaEye,
    FaShareAlt,
    FaUserCheck,
    FaHistory,
    FaEnvelope,
    FaKey,
    FaServer,
    FaFileContract,
    FaBalanceScale,
    FaCog,
    FaChevronRight,
    FaList,
    FaLock,
    FaUsers,
    FaBell,
    FaGlobe,
    FaChartLine,
    FaCertificate,
    FaClock,
    FaCheckCircle,
    FaSearch,
    FaDownload,
    FaQuestionCircle,
    FaBook,
    FaRegCopy
} from "react-icons/fa";

export default function PrivacyPolicy() {
    const [contactEmail, setEmail] = useState("");
    const [activeTab, setActiveTab] = useState("data-collection");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();

                if (data?.contactEmail) {
                    setEmail(data.contactEmail);
                }
            } catch (error) {
                console.error("Failed to load site settings", error);
            }
        };

        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        fetchSiteSettings();
        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const tabs = [
        {
            id: "data-collection",
            title: "Data Collection",
            subtitle: "Types of personal information we process",
            icon: <FaDatabase />,
            color: "#d6b87c"
        },
        {
            id: "legal-basis",
            title: "Legal Basis",
            subtitle: "GDPR compliance & lawful processing",
            icon: <FaBalanceScale />,
            color: "#c2a57a"
        },
        {
            id: "security",
            title: "Security",
            subtitle: "Protection & encryption measures",
            icon: <FaLock />,
            color: "#b8945c"
        },
        {
            id: "sharing",
            title: "Data Sharing",
            subtitle: "Third-party disclosures & transfers",
            icon: <FaShareAlt />,
            color: "#a57a42"
        },
        {
            id: "rights",
            title: "Your Rights",
            subtitle: "Data subject rights & access",
            icon: <FaUserCheck />,
            color: "#d6b87c"
        },
        {
            id: "updates",
            title: "Updates & Contact",
            subtitle: "Policy changes & how to reach us",
            icon: <FaHistory />,
            color: "#c2a57a"
        }
    ];


    const renderContent = () => {
        switch (activeTab) {
            case "data-collection":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #d6b87c 0%, #c2a57a 100%)" }}>
                                <FaDatabase />
                            </div>
                            <div>
                                <h2>Personal Data We Collect</h2>
                                <p className="jus-privacy-content-subtitle">
                                    We collect information essential for providing our legal matching services while ensuring maximum privacy protection.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-stats-grid">
                            <div className="jus-privacy-stat-card">
                                <div className="jus-privacy-stat-icon" style={{ backgroundColor: "rgba(214, 184, 124, 0.1)" }}>
                                    <FaUsers />
                                </div>
                                <div className="jus-privacy-stat-content">
                                    <h3>Identity Data</h3>
                                    <p>Name, contact details, and professional information for solicitor verification.</p>
                                    <ul>
                                        <li>Full legal name and title</li>
                                        <li>Email address & phone number</li>
                                        <li>Professional registration numbers</li>
                                        <li>Location for local matching</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="jus-privacy-stat-card">
                                <div className="jus-privacy-stat-icon" style={{ backgroundColor: "rgba(194, 165, 122, 0.1)" }}>
                                    <FaFileContract />
                                </div>
                                <div className="jus-privacy-stat-content">
                                    <h3>Service Data</h3>
                                    <p>Information related to legal services and platform interactions.</p>
                                    <ul>
                                        <li>Case details & legal requirements</li>
                                        <li>Communication records with solicitors</li>
                                        <li>Appointment preferences</li>
                                        <li>Service feedback & reviews</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="jus-privacy-stat-card">
                                <div className="jus-privacy-stat-icon" style={{ backgroundColor: "rgba(184, 148, 92, 0.1)" }}>
                                    <FaCog />
                                </div>
                                <div className="jus-privacy-stat-content">
                                    <h3>Technical Data</h3>
                                    <p>Automatically collected information for service optimization.</p>
                                    <ul>
                                        <li>Device & browser information</li>
                                        <li>IP address & approximate location</li>
                                        <li>Platform usage analytics</li>
                                        <li>Cookie & session data</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="jus-privacy-stat-card">
                                <div
                                    className="jus-privacy-stat-icon"
                                    style={{ backgroundColor: "rgba(122, 162, 214, 0.1)" }}
                                >
                                    <FaShieldAlt />
                                </div>
                                <div className="jus-privacy-stat-content">
                                    <h3>Security & Compliance Data</h3>
                                    <p>Information used to protect users, prevent fraud, and meet legal obligations.</p>
                                    <ul>
                                        <li>Login activity & authentication logs</li>
                                        <li>Consent & policy acceptance records</li>
                                        <li>Audit trails for agreements</li>
                                        <li>Regulatory & compliance data</li>
                                    </ul>
                                </div>
                            </div>

                        </div>

                        <div className="jus-privacy-compliance-section">
                            <div className="jus-privacy-compliance-header">
                                <FaCheckCircle className="jus-privacy-compliance-icon" />
                                <h3>Data Minimization Principle</h3>
                            </div>
                            <p>
                                We adhere strictly to the data minimization principle, collecting only information that is necessary, relevant,
                                and limited to what is required for the specified purposes. All data collection is transparent, with clear
                                indication of mandatory versus optional fields.
                            </p>
                        </div>
                    </div>
                );

            case "legal-basis":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #c2a57a 0%, #b8945c 100%)" }}>
                                <FaBalanceScale />
                            </div>
                            <div>
                                <h2>Legal Basis for Processing</h2>
                                <p className="jus-privacy-content-subtitle">
                                    Our data processing activities are conducted in compliance with UK GDPR requirements under specific lawful bases.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-legal-basis-cards">
                            <div className="jus-privacy-legal-card" style={{ borderTop: "4px solid #d6b87c" }}>
                                <div className="jus-privacy-legal-number">
                                    <span>01</span>
                                </div>
                                <h3>Contractual Necessity</h3>
                                <p>Processing required to perform our contractual obligations, including solicitor matching, communication facilitation, and service delivery.</p>
                                <div className="jus-privacy-legal-examples">
                                    <span>User account management</span>
                                    <span>Service provision</span>
                                    <span>Payment processing</span>
                                </div>
                            </div>

                            <div className="jus-privacy-legal-card" style={{ borderTop: "4px solid #c2a57a" }}>
                                <div className="jus-privacy-legal-number">
                                    <span>02</span>
                                </div>
                                <h3>Legitimate Interests</h3>
                                <p>Processing for our legitimate business interests, balanced against individual rights and freedoms.</p>
                                <div className="jus-privacy-legal-examples">
                                    <span>Platform security</span>
                                    <span>Fraud prevention</span>
                                    <span>Service improvement</span>
                                    <span>Network security</span>
                                </div>
                            </div>

                            <div className="jus-privacy-legal-card" style={{ borderTop: "4px solid #b8945c" }}>
                                <div className="jus-privacy-legal-number">
                                    <span>03</span>
                                </div>
                                <h3>Legal Obligation</h3>
                                <p>Processing required to comply with legal and regulatory requirements applicable to legal services.</p>
                                <div className="jus-privacy-legal-examples">
                                    <span>Anti-money laundering</span>
                                    <span>Regulatory compliance</span>
                                    <span>Court orders</span>
                                </div>
                            </div>

                            <div className="jus-privacy-legal-card" style={{ borderTop: "4px solid #a57a42" }}>
                                <div className="jus-privacy-legal-number">
                                    <span>04</span>
                                </div>
                                <h3>Consent</h3>
                                <p>Processing based on explicit, informed consent for specific purposes.</p>
                                <div className="jus-privacy-legal-examples">
                                    <span>Marketing communications</span>
                                    <span>Optional data collection</span>
                                    <span>Analytics preferences</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "security":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #b8945c 0%, #a57a42 100%)" }}>
                                <FaLock />
                            </div>
                            <div>
                                <h2>Security & Protection Measures</h2>
                                <p className="jus-privacy-content-subtitle">
                                    Multi-layered security approach designed to protect your personal data at every stage.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-security-layers">
                            <div className="jus-privacy-layer">
                                <div className="jus-privacy-layer-header">
                                    <div className="jus-privacy-layer-icon" style={{ backgroundColor: "rgba(214, 184, 124, 0.1)" }}>
                                        <FaKey />
                                    </div>
                                    <h3>Encryption & Technical Controls</h3>
                                </div>
                                <ul>
                                    <li><strong>TLS 1.3 encryption</strong> for all data in transit</li>
                                    <li><strong>AES-256 encryption</strong> for data at rest</li>
                                    <li><strong>Multi-factor authentication</strong> for professional accounts</li>
                                    <li><strong>Regular security audits</strong> & penetration testing</li>
                                </ul>
                            </div>

                            <div className="jus-privacy-layer">
                                <div className="jus-privacy-layer-header">
                                    <div className="jus-privacy-layer-icon" style={{ backgroundColor: "rgba(194, 165, 122, 0.1)" }}>
                                        <FaServer />
                                    </div>
                                    <h3>Infrastructure & Access</h3>
                                </div>
                                <ul>
                                    <li><strong>UK-based data centers</strong> with geographical redundancy</li>
                                    <li><strong>Role-based access controls</strong> with strict permissions</li>
                                    <li><strong>Zero-trust architecture</strong> implementation</li>
                                    <li><strong>Secure development</strong> life cycle practices</li>
                                </ul>
                            </div>

                            <div className="jus-privacy-layer">
                                <div className="jus-privacy-layer-header">
                                    <div className="jus-privacy-layer-icon" style={{ backgroundColor: "rgba(184, 148, 92, 0.1)" }}>
                                        <FaEye />
                                    </div>
                                    <h3>Monitoring & Response</h3>
                                </div>
                                <ul>
                                    <li><strong>24/7 security monitoring</strong> & threat detection</li>
                                    <li><strong>Automated incident response</strong> protocols</li>
                                    <li><strong>Regular vulnerability assessments</strong></li>
                                    <li><strong>Data breach response plan</strong> aligned with ICO guidelines</li>
                                </ul>
                            </div>
                        </div>

                        <div className="jus-privacy-certifications">
                            <div className="jus-privacy-certification">
                                <FaCertificate className="jus-privacy-cert-icon" />
                                <div>
                                    <h4>Compliance Standards</h4>
                                    <p>Our security practices align with ISO 27001, GDPR, and UK data protection requirements.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "sharing":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #a57a42 0%, #8c6b35 100%)" }}>
                                <FaShareAlt />
                            </div>
                            <div>
                                <h2>Data Sharing & International Transfers</h2>
                                <p className="jus-privacy-content-subtitle">
                                    Strict protocols govern when and how we share your information with third parties.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-sharing-flow">
                            <div className="jus-privacy-sharing-step">
                                <div className="jus-privacy-step-number">1</div>
                                <h3>With Legal Professionals</h3>
                                <p>Your case information is shared only with solicitors you explicitly choose to connect with. Each solicitor operates as a separate data controller under their professional regulations.</p>
                            </div>

                            <div className="jus-privacy-sharing-step">
                                <div className="jus-privacy-step-number">2</div>
                                <h3>Service Providers</h3>
                                <p>We engage specialized vendors under strict data processing agreements with GDPR-compliant safeguards:</p>
                                <ul>
                                    <li><strong>Cloud Infrastructure:</strong> AWS Europe (GDPR-compliant)</li>
                                    <li><strong>Communication:</strong> End-to-end encrypted messaging</li>
                                    <li><strong>Analytics:</strong> Anonymized, aggregated data only</li>
                                </ul>
                            </div>

                            <div className="jus-privacy-sharing-step">
                                <div className="jus-privacy-step-number">3</div>
                                <h3>Legal & Regulatory</h3>
                                <p>Disclosures required by law, including responses to valid legal process, regulatory investigations, or to protect rights, property, or safety.</p>
                            </div>
                        </div>

                        <div className="jus-privacy-transfer-safeguards">
                            <FaGlobe className="jus-privacy-globe-icon" />
                            <div>
                                <h4>International Transfer Safeguards</h4>
                                <p>All international data transfers utilize Standard Contractual Clauses (SCCs) with supplementary measures to ensure UK GDPR compliance.</p>
                            </div>
                        </div>
                    </div>
                );

            case "rights":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #d6b87c 0%, #c2a57a 100%)" }}>
                                <FaUserCheck />
                            </div>
                            <div>
                                <h2>Your Data Protection Rights</h2>
                                <p className="jus-privacy-content-subtitle">
                                    Comprehensive rights under UK data protection law with streamlined exercise procedures.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-rights-grid">
                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaSearch />
                                </div>
                                <h3>Right of Access</h3>
                                <p>Obtain confirmation of processing and a copy of your personal data (Subject Access Request).</p>
                                <div className="jus-privacy-right-detail">
                                    <FaClock />
                                    <span>Response within 30 days</span>
                                </div>
                            </div>

                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaRegCopy />
                                </div>
                                <h3>Right to Rectification</h3>
                                <p>Request correction of inaccurate or incomplete personal information we hold about you.</p>
                                <div className="jus-privacy-right-detail">
                                    <FaCheckCircle />
                                    <span>Corrected without undue delay</span>
                                </div>
                            </div>

                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaHistory />
                                </div>
                                <h3>Right to Erasure</h3>
                                <p>Request deletion of your personal data in specific circumstances ("right to be forgotten").</p>
                                <div className="jus-privacy-right-detail">
                                    <FaClock />
                                    <span>Processed within 30 days</span>
                                </div>
                            </div>

                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaFileContract />
                                </div>
                                <h3>Right to Restriction</h3>
                                <p>Request limitation of processing in certain situations while verification is pending.</p>
                                <div className="jus-privacy-right-detail">
                                    <FaCheckCircle />
                                    <span>Applied immediately upon request</span>
                                </div>
                            </div>

                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaBalanceScale />
                                </div>
                                <h3>Right to Object</h3>
                                <p>Object to processing based on legitimate interests or for direct marketing purposes.</p>
                                <div className="jus-privacy-right-detail">
                                    <FaCheckCircle />
                                    <span>Processing ceases upon objection</span>
                                </div>
                            </div>

                            <div className="jus-privacy-right-card">
                                <div className="jus-privacy-right-icon">
                                    <FaDatabase />
                                </div>
                                <h3>Right to Portability</h3>
                                <p>Receive your personal data in a structured, commonly used, machine-readable format.</p>
                                <div className="jus-privacy-right-detail">
                                    <FaClock />
                                    <span>Provided within 30 days</span>
                                </div>
                            </div>
                        </div>

                        <div className="jus-privacy-exercise-box">
                            <div className="jus-privacy-exercise-content">
                                <FaBell className="jus-privacy-exercise-icon" />
                                <div>
                                    <h4>Exercising Your Rights</h4>
                                    <p>
                                        Contact our Data Protection Officer at <strong>{contactEmail || "dpo@justivon.com"}</strong> to exercise any
                                        of these rights. We respond to all valid requests within 30 calendar days as required by law.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "updates":
                return (
                    <div className="jus-privacy-tab-content">
                        <div className="jus-privacy-content-header">
                            <div className="jus-privacy-content-icon" style={{ background: "linear-gradient(135deg, #c2a57a 0%, #b8945c 100%)" }}>
                                <FaHistory />
                            </div>
                            <div>
                                <h2>Policy Updates & Contact</h2>
                                <p className="jus-privacy-content-subtitle">
                                    Continuous improvement and transparent communication regarding our privacy practices.
                                </p>
                            </div>
                        </div>

                        <div className="jus-privacy-update-timeline">
                            <div className="jus-privacy-timeline-item">
                                <div className="jus-privacy-timeline-dot"></div>
                                <div className="jus-privacy-timeline-content">
                                    <h3>Annual Review Cycle</h3>
                                    <p>This privacy policy undergoes comprehensive annual review, with additional reviews triggered by significant regulatory changes or platform updates.</p>
                                </div>
                            </div>

                            <div className="jus-privacy-timeline-item">
                                <div className="jus-privacy-timeline-dot"></div>
                                <div className="jus-privacy-timeline-content">
                                    <h3>Change Notification Protocol</h3>
                                    <p>Material changes to this policy are communicated to users via email and platform notifications at least 30 days before implementation.</p>
                                </div>
                            </div>

                            <div className="jus-privacy-timeline-item">
                                <div className="jus-privacy-timeline-dot"></div>
                                <div className="jus-privacy-timeline-content">
                                    <h3>Version History</h3>
                                    <p>Previous versions of this policy are archived and available upon request to ensure transparency in our privacy evolution.</p>
                                </div>
                            </div>
                        </div>

                        <div className="jus-privacy-contact-section">
                            <div className="jus-privacy-contact-card">
                                <div className="jus-privacy-contact-header">
                                    <FaEnvelope className="jus-privacy-contact-main-icon" />
                                    <h3>Contact Information</h3>
                                </div>
                                <div className="jus-privacy-contact-details">
                                    <div className="jus-privacy-contact-item">
                                        <strong>Data Protection Officer</strong>
                                        <p>{contactEmail || "dpo@justivon.com"}</p>
                                    </div>
                                    <div className="jus-privacy-contact-item">
                                        <strong>Response Time</strong>
                                        <p>Within 30 days for data subject requests</p>
                                    </div>
                                    <div className="jus-privacy-contact-item">
                                        <strong>Complaint Resolution</strong>
                                        <p>We aim to resolve privacy concerns within 14 business days</p>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-privacy-regulatory-card">
                                <div className="jus-privacy-regulatory-header">
                                    <FaShieldAlt className="jus-privacy-regulatory-icon" />
                                    <h3>Regulatory Oversight</h3>
                                </div>
                                <p>
                                    You have the right to lodge a complaint with the Information Commissioner's Office (ICO),
                                    the UK supervisory authority for data protection issues, if you believe our processing
                                    of your personal data infringes data protection laws.
                                </p>
                                <a
                                    href="https://www.ico.org.uk"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="jus-privacy-ico-link"
                                >
                                    Visit ICO Website <FaChevronRight />
                                </a>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="jus-privacy-container">
            <Header />

            {/* Hero Section */}
            <section className="jus-privacy-hero">
                <div className="jus-privacy-hero-content">
                    <div className="jus-privacy-hero-badge">
                        <FaShieldAlt />
                        <span>GDPR Compliant</span>
                    </div>
                    <h1 className="jus-privacy-hero-title">
                        Privacy <span className="jus-privacy-highlight">Policy</span>
                    </h1>
                    <p className="jus-privacy-hero-subtitle">
                        Justivon's commitment to protecting your personal data in accordance with UK GDPR and Data Protection Act 2018.
                    </p>
                    <div className="jus-privacy-hero-meta">
                        <div className="jus-privacy-meta-item">
                            <FaClock />
                            <span>Last Updated: March 2024</span>
                        </div>
                        <div className="jus-privacy-meta-item">
                            <FaFileContract />
                            <span>Version 2.1</span>
                        </div>
                        <div className="jus-privacy-meta-item">
                            <FaGlobe />
                            <span>UK GDPR Compliant</span>
                        </div>
                    </div>
                </div>
                <div className="jus-privacy-hero-pattern"></div>
            </section>

            {/* Main Content */}
            <main className="jus-privacy-main">
                <div className="jus-privacy-content-wrapper">
                    {/* Navigation Sidebar */}
                    <nav className="jus-privacy-sidebar">
                        <div className="jus-privacy-sidebar-header">
                            <h3>Privacy Policy Sections</h3>
                            <p>Navigate through our comprehensive privacy framework</p>
                        </div>
                        <div className="jus-privacy-sidebar-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`jus-privacy-sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <div
                                        className="jus-privacy-tab-indicator"
                                        style={{ backgroundColor: tab.color }}
                                    ></div>
                                    <div className="jus-privacy-tab-content-wrapper">
                                        <div className="jus-privacy-tab-icon" style={{ color: tab.color }}>
                                            {tab.icon}
                                        </div>
                                        <div className="jus-privacy-tab-text">
                                            <span className="jus-privacy-tab-title">{tab.title}</span>
                                            <span className="jus-privacy-tab-subtitle">{tab.subtitle}</span>
                                        </div>
                                        {activeTab === tab.id && (
                                            <FaChevronRight className="jus-privacy-tab-arrow" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </nav>

                    {/* Content Area */}
                    <div className="jus-privacy-content-container">
                        <div className="jus-privacy-content-area">
                            {renderContent()}
                        </div>
                    </div>
                </div>

                {/* Resources Section */}
                <div className="jus-privacy-resources">
                    <div className="jus-privacy-resources-header">
                        <h3>Privacy Resources</h3>
                        <p>Additional tools and information for data protection</p>
                    </div>
                    <div className="jus-privacy-resources-grid">
                        <a href="/faq" className="jus-privacy-resource-card">
                            <div className="jus-privacy-resource-icon">
                                <FaQuestionCircle />
                            </div>
                            <div className="jus-privacy-resource-content">
                                <h4>Privacy FAQ</h4>
                                <p>Common questions about data protection</p>
                            </div>
                            <FaChevronRight className="jus-privacy-resource-arrow" />
                        </a>

                        <a href="#download" className="jus-privacy-resource-card">
                            <div className="jus-privacy-resource-icon">
                                <FaDownload />
                            </div>
                            <div className="jus-privacy-resource-content">
                                <h4>Download Policy</h4>
                                <p>PDF version for your records</p>
                            </div>
                            <FaChevronRight className="jus-privacy-resource-arrow" />
                        </a>

                        <button
                            className="jus-cookies-resource-card"
                            onClick={() => {
                                setActiveTab("rights");
                                window.scrollTo({
                                    top: 490, // adjust if header height changes
                                    behavior: "smooth"
                                });
                            }}
                        >
                            <div className="jus-privacy-resource-icon">
                                <FaUserCheck />
                            </div>
                            <div className="jus-privacy-resource-content">
                                <h4>Exercise Rights</h4>
                                <p>Submit a data subject request</p>
                            </div>
                            <FaChevronRight className="jus-privacy-resource-arrow" />
                        </button>

                        <a
                            href={`mailto:${contactEmail || "dpo@justivon.com"}`}
                            className="jus-privacy-resource-card"
                        >
                            <div className="jus-privacy-resource-icon">
                                <FaEnvelope />
                            </div>
                            <div className="jus-privacy-resource-content">
                                <h4>Contact DPO</h4>
                                <p>Reach our Data Protection Officer</p>
                            </div>
                            <FaChevronRight className="jus-privacy-resource-arrow" />
                        </a>
                    </div>
                </div>
            </main>
            <ChatBot />
            <Footer />
        </div>
    );
}